import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { collectionsAPI, uploadAPI } from '../services/api';
import { ArrowLeft, Upload, X } from 'lucide-react';

const CreateCollection = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { addCollection, updateCollection, collections } = useApp();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [imagePreview, setImagePreview] = useState('');

  useEffect(() => {
    if (!isEditMode) return;

    const loadCollection = async () => {
      try {
        setIsLoading(true);
        const existing = collections.find((c) => c.id === id);

        if (existing) {
          setFormData({
            name: existing.name || '',
            description: existing.description || '',
            image: existing.image || ''
          });
          setImagePreview(existing.image || '');
          return;
        }

        const response = await collectionsAPI.getById(id);
        if (response.success && response.collection) {
          setFormData({
            name: response.collection.name || '',
            description: response.collection.description || '',
            image: response.collection.image || ''
          });
          setImagePreview(response.collection.image || '');
        } else {
          setErrors({ general: 'Collection not found' });
        }
      } catch (error) {
        console.error('Error loading collection:', error);
        setErrors({ general: error.message || 'Failed to load collection' });
      } finally {
        setIsLoading(false);
      }
    };

    loadCollection();
  }, [id, isEditMode, collections]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, image: 'Please select a valid image file' }));
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, image: 'Image size should be less than 5MB' }));
        return;
      }

      setIsSubmitting(true);
      
      try {
        // Upload to Cloudinary
        const response = await uploadAPI.uploadImage(file);
        const cloudinaryUrl = response.image.url;
        
        setImagePreview(cloudinaryUrl);
        setFormData(prev => ({ ...prev, image: cloudinaryUrl }));
        setErrors(prev => ({ ...prev, image: '' }));
        
      } catch (error) {
        console.error('Image upload error:', error);
        setErrors(prev => ({ ...prev, image: 'Failed to upload image. Please try again.' }));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setFormData(prev => ({ ...prev, image: url }));
    setImagePreview(url);
  };

  const clearImage = () => {
    setImagePreview('');
    setFormData(prev => ({ ...prev, image: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Collection name is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.image.trim()) {
      newErrors.image = 'Image URL is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const collectionData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        image: formData.image.trim()
      };
      
      if (isEditMode) {
        await updateCollection(id, collectionData);
      } else {
        await addCollection(collectionData);
      }
      navigate('/collections');
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} collection:`, error);
      setErrors({
        general: error.message || `Failed to ${isEditMode ? 'update' : 'create'} collection. Please try again.`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/collections');
  };

  const sampleImages = [
    'https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=300&h=200&fit=crop',
    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=200&fit=crop',
    'https://images.unsplash.com/photo-1445205170230-053b83016050?w=300&h=200&fit=crop',
    'https://images.unsplash.com/photo-1434389677669-e08b4cac3105?w=300&h=200&fit=crop'
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <button 
          onClick={handleCancel}
          className="inline-flex items-center text-indigo-600 hover:text-indigo-700 mb-4"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to Collections
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? 'Edit Collection' : 'Create New Collection'}
        </h1>
        <p className="text-gray-600 mt-1">
          {isEditMode
            ? 'Update your collection details'
            : 'Add a new collection to organize your products'}
        </p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {errors.general && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {errors.general}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Collection Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Collection Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., Summer Collection 2024"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.name && (
              <p className="text-red-600 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              rows={4}
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe your collection..."
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                errors.description ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.description && (
              <p className="text-red-600 text-sm mt-1">{errors.description}</p>
            )}
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Collection Image *
            </label>
            
            {/* File Upload */}
            <div className="mb-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-300 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">
                    Click to upload image
                  </span>
                  <span className="text-xs text-gray-500 mt-1">
                    PNG, JPG up to 5MB
                  </span>
                </label>
              </div>
            </div>

            {/* URL Input */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">Or enter image URL:</label>
              <input
                type="url"
                value={formData.image}
                onChange={handleImageUrlChange}
                placeholder="https://example.com/image.jpg"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  errors.image ? 'border-red-300' : 'border-gray-300'
                }`}
              />
            </div>

            {errors.image && (
              <p className="text-red-600 text-sm mb-4">{errors.image}</p>
            )}
            
            {/* Sample Images */}
            <div className="mt-3">
              <p className="text-sm text-gray-600 mb-2">Or choose from these sample images:</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {sampleImages.map((imageUrl, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ ...prev, image: imageUrl }));
                      setImagePreview(imageUrl);
                    }}
                    className={`relative border-2 rounded-lg overflow-hidden transition-all ${
                      formData.image === imageUrl ? 'border-indigo-500' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img src={imageUrl} alt={`Sample ${index + 1}`} className="w-full h-16 object-cover" />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Preview */}
          {(imagePreview || formData.image) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="relative">
                  <img 
                    src={imagePreview || formData.image} 
                    alt="Collection preview"
                    className="w-full h-32 object-cover rounded-lg mb-2"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      setErrors(prev => ({ ...prev, image: 'Invalid image URL' }));
                    }}
                  />
                  <button
                    type="button"
                    onClick={clearImage}
                    className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X size={16} />
                  </button>
                </div>
                <h3 className="font-semibold text-gray-900">{formData.name || 'Collection Name'}</h3>
                <p className="text-sm text-gray-600">{formData.description || 'Collection description'}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-4 pt-6">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-lg font-medium transition-colors ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting
                ? (isEditMode ? 'Saving...' : 'Creating...')
                : (isEditMode ? 'Save Changes' : 'Create Collection')}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCollection; 